/**
 * Break a monologue into individual sentences based on punctuation
 * @param {Object} monologue - transcript monologue with an element for each character
 * @returns {Array} Array of sentences, where each sentence is an array of elements
 */
const chunkIntoSentences = (monologue) => {
  const sentences = [];
  let currentSentence = [];

  for (const element of monologue.elements) {
    currentSentence.push(element);

    if (element.type === 'punct' && ['.', '!', '?'].includes(element.value)) {
      sentences.push(currentSentence);
      currentSentence = [];
    }
  }

  // Add any remaining elements as a final sentence
  if (currentSentence.length > 0) {
    sentences.push(currentSentence);
  }

  return sentences;
};

/**
 * Process raw transcript JSON into an array of timestamped sentences
 * Transcripts are composed of monologues composed of single character elements
 * that are timestamped.
 * @param {Object} rawTranscript - Raw transcript object with monologues array
 * @returns {Array} Array of sentences with the text, start_ts and end_ts
 */
export const processRawTranscriptIntoSentences = (rawTranscript) => {
  const result = [];

  // Expect one speaker in the video, get their monologue
  if (!rawTranscript?.monologues?.[0]) {
    return result;
  }

  const sentences = chunkIntoSentences(rawTranscript.monologues[0]);

  for (const sentence of sentences) {
    // Combine all element values to get the full text
    const text = sentence
      .map((e) => e.value)
      .join('')
      .trim();

    // Not all characters are timestamped, so scan to find the first and last ones
    let startTime = null;
    for (const element of sentence) {
      if (element.ts !== undefined) {
        startTime = element.ts;
        break;
      }
    }

    let endTime = null;
    for (let i = sentence.length - 1; i >= 0; i--) {
      if (sentence[i].end_ts !== undefined) {
        endTime = sentence[i].end_ts;
        break;
      }
    }

    // Only add if we have text and timestamps
    if (text && startTime !== null && endTime !== null) {
      result.push({
        end_ts: endTime,
        start_ts: startTime,
        text,
      });
    }
  }

  return result;
};

/**
 * Format timestamp in seconds to mm:ss or hh:mm:ss format
 * @param {number} seconds - Time in seconds
 * @returns {string} Formatted timestamp
 */
export const formatTimestamp = (seconds) => {
  if (!seconds && seconds !== 0) return '00:00';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Helper to highlight matched text using mark tags
 * @param {string} text - The text to highlight
 * @param {string} query - The search query to highlight
 * @returns {Array} Array of text parts and mark elements
 */
export const highlightText = (text, query) => {
  if (!query) return text;

  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const parts = [];
  let lastIndex = 0;
  let index = lowerText.indexOf(lowerQuery);

  while (index !== -1 && lowerQuery.length > 0) {
    if (index > lastIndex) {
      parts.push(text.slice(lastIndex, index));
    }
    parts.push(<mark key={index}>{text.slice(index, index + lowerQuery.length)}</mark>);
    lastIndex = index + lowerQuery.length;
    index = lowerText.indexOf(lowerQuery, lastIndex);
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
};
